import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Checkbox } from 'primereact/checkbox';
import { adminApi } from '../../../api/adminApi';
import MapPicker from '../../common/MapPicker';

const ESTADO_INICIAL = {
  nombre: '',
  cuit: '',
  email: '',
  telefono: '',
  direccion: '',
  localidad: '',
  responsable: '',
  horario: '',
  ubicacion: null,
};

export default function PanelCasinos() {
  const toast = useRef(null);
  const [casinos, setCasinos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null); // null | objeto casino | 'nuevo'
  const [form, setForm] = useState(ESTADO_INICIAL);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    setCargando(true);
    try {
      const res = await adminApi.getCasinos();
      setCasinos(res.data);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los casinos' });
    } finally {
      setCargando(false);
    }
  }

  const casinosFiltrados = casinos.filter((c) => {
    if (!mostrarInactivos && c.activo === false) return false;
    const q = filtro.toLowerCase();
    if (!q) return true;
    return (
      (c.nombre || '').toLowerCase().includes(q) ||
      (c.localidad || '').toLowerCase().includes(q) ||
      (c.responsable || '').toLowerCase().includes(q)
    );
  });

  function abrirNuevo() {
    setEditando('nuevo');
    setForm(ESTADO_INICIAL);
    setErrores({});
  }

  function abrirEditar(casino) {
    setEditando(casino);
    setForm({
      nombre: casino.nombre || '',
      cuit: casino.cuit || '',
      email: casino.email || '',
      telefono: casino.telefono || '',
      direccion: casino.direccion || '',
      localidad: casino.localidad || '',
      responsable: casino.responsable || '',
      horario: casino.horario || '',
      ubicacion: casino.ubicacion || null,
    });
    setErrores({});
  }

  function cerrarDialogo() {
    setEditando(null);
    setForm(ESTADO_INICIAL);
    setErrores({});
  }

  function setCampo(k, v) {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errores[k]) setErrores((prev) => ({ ...prev, [k]: undefined }));
  }

  function validar() {
    const err = {};
    if (!form.nombre.trim()) err.nombre = 'Obligatorio';
    if (!form.direccion.trim()) err.direccion = 'Obligatorio';
    if (!form.localidad.trim()) err.localidad = 'Obligatorio';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = 'Email inválido';
    setErrores(err);
    return Object.keys(err).length === 0;
  }

  async function guardar() {
    if (!validar()) return;
    setGuardando(true);
    try {
      if (editando === 'nuevo') {
        await adminApi.crearCasino(form);
        toast.current?.show({ severity: 'success', summary: 'Casino creado', detail: form.nombre });
      } else {
        await adminApi.editarCasino(editando.id, form);
        toast.current?.show({ severity: 'success', summary: 'Casino actualizado', detail: form.nombre });
      }
      cerrarDialogo();
      await cargar();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detalles?.join(', ') || 'No se pudo guardar';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg });
    } finally {
      setGuardando(false);
    }
  }

  function confirmarDesactivar(casino) {
    confirmDialog({
      message: `¿Desactivar el casino "${casino.nombre}"? No se borra, solo deja de estar disponible para asignar a nuevos usuarios.`,
      header: 'Confirmar desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactivar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => desactivar(casino),
    });
  }

  async function desactivar(casino) {
    try {
      const res = await adminApi.desactivarCasino(casino.id);
      const detalle = res.data.usuariosAfectados > 0
        ? `Casino desactivado. ${res.data.usuariosAfectados} usuario(s) lo tenían asignado.`
        : 'Casino desactivado';
      toast.current?.show({ severity: 'success', summary: 'Listo', detail: detalle });
      await cargar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo desactivar' });
    }
  }

  async function reactivar(casino) {
    try {
      await adminApi.reactivarCasino(casino.id);
      toast.current?.show({ severity: 'success', summary: 'Casino reactivado', detail: casino.nombre });
      await cargar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo reactivar' });
    }
  }

  // ─── Column templates ─────────────────────────────────────────────────────
  const ubicacionTpl = (row) => row.ubicacion
    ? <i className="pi pi-check-circle" style={{ color: '#43a047', fontSize: 18 }} title={`${row.ubicacion.lat}, ${row.ubicacion.lng}`} />
    : <span style={{ color: '#bbb' }}>—</span>;

  const estadoTpl = (row) => (
    <Tag value={row.activo === false ? 'Inactivo' : 'Activo'} severity={row.activo === false ? 'secondary' : 'success'} />
  );

  const accionesTpl = (row) => (
    <div style={{ display: 'flex', gap: 4 }}>
      <Button icon="pi pi-pencil" rounded text severity="info" onClick={() => abrirEditar(row)} tooltip="Editar" tooltipOptions={{ position: 'top' }} />
      {row.activo === false ? (
        <Button icon="pi pi-refresh" rounded text severity="success" onClick={() => reactivar(row)} tooltip="Reactivar" tooltipOptions={{ position: 'top' }} />
      ) : (
        <Button icon="pi pi-ban" rounded text severity="danger" onClick={() => confirmarDesactivar(row)} tooltip="Desactivar" tooltipOptions={{ position: 'top' }} />
      )}
    </div>
  );

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Casinos</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Checkbox inputId="inactivos" checked={mostrarInactivos} onChange={(e) => setMostrarInactivos(e.checked)} />
            <label htmlFor="inactivos" style={{ fontSize: 14, cursor: 'pointer' }}>Mostrar inactivos</label>
          </div>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar..." style={{ width: 240 }} />
          </span>
          <Button label="Nuevo casino" icon="pi pi-plus" onClick={abrirNuevo} />
        </div>
      </div>

      <DataTable
        value={casinosFiltrados}
        loading={cargando}
        paginator
        rows={15}
        rowsPerPageOptions={[10, 15, 30, 50]}
        emptyMessage="No hay casinos registrados"
        stripedRows
        size="small"
      >
        <Column field="nombre" header="Nombre" sortable style={{ minWidth: 200 }} />
        <Column field="localidad" header="Localidad" sortable style={{ minWidth: 140 }} />
        <Column field="direccion" header="Dirección" style={{ minWidth: 200 }} />
        <Column field="responsable" header="Responsable" style={{ minWidth: 150 }} />
        <Column header="Ubic." body={ubicacionTpl} style={{ width: 80, textAlign: 'center' }} />
        <Column header="Estado" body={estadoTpl} sortable field="activo" style={{ width: 110 }} />
        <Column header="Acciones" body={accionesTpl} style={{ width: 140 }} />
      </DataTable>

      <Dialog
        header={editando === 'nuevo' ? 'Nuevo casino' : `Editar: ${editando?.nombre || ''}`}
        visible={!!editando}
        style={{ width: '90vw', maxWidth: 800 }}
        modal
        onHide={cerrarDialogo}
        footer={
          <div>
            <Button label="Cancelar" icon="pi pi-times" onClick={cerrarDialogo} text disabled={guardando} />
            <Button label={guardando ? 'Guardando…' : 'Guardar'} icon="pi pi-check" onClick={guardar} disabled={guardando} />
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Sección: datos básicos */}
          <section>
            <h4 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Datos básicos</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <Campo label="Nombre *" error={errores.nombre}>
                <InputText value={form.nombre} onChange={(e) => setCampo('nombre', e.target.value)} className={errores.nombre ? 'p-invalid' : ''} />
              </Campo>
              <Campo label="CUIT" error={errores.cuit}>
                <InputText value={form.cuit} onChange={(e) => setCampo('cuit', e.target.value)} placeholder="30-12345678-9" />
              </Campo>
              <Campo label="Email" error={errores.email}>
                <InputText value={form.email} onChange={(e) => setCampo('email', e.target.value)} className={errores.email ? 'p-invalid' : ''} />
              </Campo>
              <Campo label="Teléfono" error={errores.telefono}>
                <InputText value={form.telefono} onChange={(e) => setCampo('telefono', e.target.value)} />
              </Campo>
            </div>
          </section>

          {/* Sección: ubicación */}
          <section>
            <h4 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Ubicación física</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
              <Campo label="Dirección *" error={errores.direccion}>
                <InputText value={form.direccion} onChange={(e) => setCampo('direccion', e.target.value)} className={errores.direccion ? 'p-invalid' : ''} />
              </Campo>
              <Campo label="Localidad *" error={errores.localidad}>
                <InputText value={form.localidad} onChange={(e) => setCampo('localidad', e.target.value)} className={errores.localidad ? 'p-invalid' : ''} />
              </Campo>
            </div>
            <MapPicker
              value={form.ubicacion}
              onChange={(v) => setCampo('ubicacion', v)}
              height={280}
            />
          </section>

          {/* Sección: operatoria */}
          <section>
            <h4 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Operatoria</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <Campo label="Responsable">
                <InputText value={form.responsable} onChange={(e) => setCampo('responsable', e.target.value)} />
              </Campo>
              <Campo label="Horario">
                <InputText value={form.horario} onChange={(e) => setCampo('horario', e.target.value)} placeholder="Lun-Vie 9-18hs" />
              </Campo>
            </div>
          </section>
        </div>
      </Dialog>
    </div>
  );
}

function Campo({ label, children, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>{label}</label>
      {children}
      {error && <small style={{ color: '#c62828' }}>{error}</small>}
    </div>
  );
}
